// apps/dashboard/src/components/forms/BirthYearInput.tsx
//
// Shared 4-digit birth-year input. Replaces four duplicated `type="number"`
// fields (ConsentPromptDialog, PreferensiTab, register/page, IdentityStep)
// that all shared the same bugs:
//   - the mouse wheel silently changes the value over a number input
//   - number inputs accept "e", "E", "+", "-", "." and scientific notation
//     like "2e3"
//   - the browser's spinner arrows serve no purpose for a 4-digit year
//   - some call sites were narrow enough (w-24) to clip the approved
//     "Contoh: 2006" placeholder — the bug the PO actually reported
//
// This component owns sanitization only (digits-only, max 4 chars) and the
// DOM shape (type="text", inputMode, pattern, maxLength, autoComplete). It
// stays controlled and does not own parsing/validation/submit — callers keep
// their own state and call into consent-age.ts's isValidBirthYear /
// isMarketingAdult, since two callers (register, IdentityStep) need extra
// onChange side effects (auto-untick a checkbox) that don't belong in a
// generic input.
'use client';

import { cn } from '@/lib/utils';

interface BirthYearInputProps {
  id: string;
  value: string;
  onChange: (digits: string) => void;
  label: string;
  placeholder: string;
  helperText?: React.ReactNode;
  error?: boolean;
  errorText?: string;
  disabled?: boolean;
  labelClassName?: string;
  inputClassName?: string;
  helperClassName?: string;
  errorClassName?: string;
}

const BASE_INPUT_CLASS = 'w-40 px-3 py-2 text-sm text-slate-900 bg-white outline-none transition-colors';
const DEFAULT_LABEL_CLASS = 'block text-xs font-medium text-slate-700';
const DEFAULT_HELPER_CLASS = 'text-[11px] text-slate-500';
const DEFAULT_ERROR_CLASS = 'text-xs text-red-600';

// Callers that need a fully custom message below the field (e.g. register/
// page.tsx and IdentityStep's conditional underage-notice-vs-hint, which
// carries its own data-testid and color per surface) simply omit
// helperText/errorText and render their own <p> after <BirthYearInput>.
export function BirthYearInput({
  id,
  value,
  onChange,
  label,
  placeholder,
  helperText,
  error = false,
  errorText,
  disabled = false,
  labelClassName,
  inputClassName,
  helperClassName,
  errorClassName,
}: BirthYearInputProps) {
  const helperId = `${id}-helper`;
  const errorId = `${id}-error`;
  const showError = error && !!errorText;
  const describedBy = showError ? errorId : helperText ? helperId : undefined;

  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className={labelClassName ?? DEFAULT_LABEL_CLASS}>
        {label}
      </label>
      <input
        id={id}
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        maxLength={4}
        autoComplete="bday-year"
        value={value}
        onChange={(e) => onChange(e.target.value.replace(/\D/g, '').slice(0, 4))}
        placeholder={placeholder}
        disabled={disabled}
        aria-invalid={error}
        aria-describedby={describedBy}
        className={cn(BASE_INPUT_CLASS, inputClassName)}
      />
      {showError ? (
        <p id={errorId} className={errorClassName ?? DEFAULT_ERROR_CLASS}>
          {errorText}
        </p>
      ) : helperText ? (
        <p id={helperId} className={helperClassName ?? DEFAULT_HELPER_CLASS}>
          {helperText}
        </p>
      ) : null}
    </div>
  );
}
